/**
 * Cloud API -> UI model adapters.
 *
 * The fleet models in @glyph/protocol-ts (`Me`, `Device`, `Member`, `AlertRow`, `Recording`,
 * `Release`, `Rollout`, `AuditEntry`, `AlertChannels`) were written as stand-ins before the cloud
 * API existed; the API's response models (cloud/glyph_cloud/api/schemas.py) are now the source
 * of truth. Everything the console reads from `/api/*` and `/gw/browser` is normalised here, once,
 * so the screens keep consuming the models the MockClient and the local shell already produce.
 * Documents already in model shape (the MockClient's) pass through untouched.
 * Timestamps in the models are unix seconds (what `fmtAgo` / `fmtTs` expect); the API sends ISO.
 */
import type {
  AlertChannels,
  AlertRow,
  AuditEntry,
  CaptionSchedule,
  CaptionScheduleState,
  Device,
  DeviceId,
  DeviceState,
  Me,
  Member,
  MemberScope,
  ConsoleRole,
  Preset,
  Recording,
  Release,
  Role,
  Rollout,
  RolloutDevice,
  ShowEvent,
  Topic,
  UpdateState,
  UpdateStatus,
} from '@glyph/protocol-ts';
import type { ClientEvent } from '../client/types';

type Raw = Record<string, any>;
const obj = (x: unknown): Raw => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Raw) : {});
const arr = (x: unknown): Raw[] => (Array.isArray(x) ? x.map(obj) : []);
const num = (x: unknown, d = 0): number => (typeof x === 'number' && Number.isFinite(x) ? x : d);
const numOrNull = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);
const str = (x: unknown, d = ''): string => (typeof x === 'string' ? x : d);
const strOrNull = (x: unknown): string | null => (typeof x === 'string' && x !== '' ? x : null);
/** ISO date (or epoch seconds) -> unix seconds, null when absent. */
export const secs = (x: unknown): number | null => {
  if (typeof x === 'number' && Number.isFinite(x)) return x > 1e12 ? x / 1000 : x;
  if (typeof x === 'string' && x) {
    const t = Date.parse(x);
    return Number.isFinite(t) ? t / 1000 : null;
  }
  return null;
};

const ROLE_RANK: Record<string, number> = { viewer: 0, room_operator: 0.5, operator: 1, event_admin: 1.5, admin: 2, owner: 3, platform: 4 };
const asRole = (x: unknown): ConsoleRole => (typeof x === 'string' && x in ROLE_RANK ? (x as ConsoleRole) : 'viewer');
const asScope = (x: unknown): MemberScope | null => {
  const o = obj(x);
  if (!x || typeof x !== 'object') return null;
  const ids = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);
  return { devices: ids(o.devices) as DeviceId[], events: ids(o.events) };
};

// ---- session --------------------------------------------------------------------------------------

/** `GET /api/auth/me` / `login.user` (MeOut). The console shows one org: the platform org for platform staff, else the highest-role membership. */
export function normalizeMe(raw: unknown): Me {
  const m = obj(raw);
  if (m.org && typeof m.role === 'string') return m as Me;
  const memberships = arr(m.memberships);
  const platform = !!m.platform;
  const pick =
    memberships.find((x) => platform && x.org_kind === 'platform') ??
    [...memberships].sort((a, b) => (ROLE_RANK[str(b.role)] ?? 0) - (ROLE_RANK[str(a.role)] ?? 0))[0];
  return {
    id: str(m.id),
    name: str(m.name) || str(m.email) || str(m.username),
    email: str(m.email),
    username: strOrNull(m.username),
    role: platform ? 'platform' : asRole(pick?.role),
    scope: platform ? null : asScope(pick?.scope),
    org: { id: str(pick?.org_id), name: str(pick?.org_name), kind: pick?.org_kind === 'platform' ? 'platform' : 'customer' },
    mfa_enrolled: !!m.totp_enabled,
  };
}

/** Org the console acts in (members, invites, events): same choice as `normalizeMe`. */
export function orgIdOf(raw: unknown): string {
  return normalizeMe(raw).org.id;
}

// ---- devices --------------------------------------------------------------------------------------

/** `DeviceOut` (+ its `live` redis hash: telemetry, health, update, lan_ips, hotspot_ssid, ...). */
/** Update phases that mean a release is being fetched (the unit keeps captioning) vs. the install itself. */
export const DOWNLOAD_STATES = ['checking', 'downloading', 'verifying', 'extracting'];
export const INSTALL_STATES = ['installing', 'switching', 'restarting'];
/** One rule for the fleet state in every shell: offline, locked, then the update phase, then the engine. A unit in
 * its post-update health check is running normally, so it shows live/standby (the Overview adds the note). */
export function fleetState(online: boolean, locked: boolean, updateState: string | null | undefined, running: boolean): DeviceState {
  if (!online) return 'offline';
  if (locked) return 'locked';
  if (updateState && INSTALL_STATES.includes(updateState)) return 'updating';
  if (updateState && DOWNLOAD_STATES.includes(updateState)) return 'downloading';
  return running ? 'live' : 'standby';
}

/** The update state worth showing. A unit keeps the last state of its last attempt; when that says "failed" (or
 * "error") but the unit runs exactly the version it was trying to install, the install did land and only the record is
 * stale (Room 313, 2026-10-25: a 0.1.10 agent called its own successful switch a failure). Shown as done. */
export function effectiveUpdateState(u: { state?: unknown; current_version?: unknown; target_version?: unknown; staged_version?: unknown }): string | null {
  const state = typeof u.state === 'string' && u.state ? u.state : null;
  if (state !== 'failed' && state !== 'error') return state;
  const cur = String(u.current_version ?? '').split('-')[0];
  const want = String(u.target_version || u.staged_version || '').split('-')[0];
  return cur && want && cur === want ? 'success' : state;
}

/** Telemetry arrives every 30 s; a row older than 90 s means the unit stopped reporting (the meter must not freeze on it). */
const TELEMETRY_STALE_S = 90;
export function telemetryFresh(ts: unknown, now = Date.now() / 1000): boolean {
  return typeof ts !== 'number' || now - ts < TELEMETRY_STALE_S;
}

export function normalizeFleetDevice(raw: unknown): Device {
  const d = obj(raw);
  if (typeof d.state === 'string' && 'uplink' in d) return d as Device;
  const live = obj(d.live);
  const tele = obj(live.telemetry);
  const online = !!d.online;
  const locked = !!d.locked;
  const update = obj(live.update);
  const status = (['pending', 'provisioned', 'active', 'revoked', 'retired'] as const).includes(d.status) ? d.status : 'active';
  const lanIps = Array.isArray(live.lan_ips) ? live.lan_ips.map(String) : [];
  const uplink = (['eth', 'wifi', 'lte', 'none'] as const).includes(tele.uplink) ? tele.uplink : online ? 'eth' : 'none';
  const assigned = !!d.org_id;
  return {
    id: str(d.id),
    serial: str(d.serial),
    name: str(d.name) || str(d.serial) || 'Glyph G1',
    status,
    state: fleetState(online, locked, strOrNull(update.state), !!tele.engine_running),
    online,
    last_seen: secs(d.last_seen_at),
    agent_version: str(d.agent_version ?? live.agent_version),
    slot: live.slot === 'B' ? 'B' : 'A',
    org_id: str(d.org_id),
    assignment: assigned ? { org_name: str(d.org_name), event_id: strOrNull(d.event_id), event_name: strOrNull(d.event_name) } : null,
    // 'running' | 'idle' | 'offline': what the captions pipeline is doing, never 'live' for a unit that is off
    asr_model: !online ? 'offline' : tele.engine_running ? 'running' : 'idle',
    dg_rtt_ms: numOrNull(tele.dg_latency_ms),
    cloud_rtt_ms: numOrNull(live.cloud_rtt_ms),
    update_state: effectiveUpdateState(update),
    update_target: strOrNull(update.target_version) || strOrNull(update.staged_version) || strOrNull(update.current_version),
    update_pct: numOrNull(update.progress_pct),
    update_error: strOrNull(update.last_error),
    update_finished_at: numOrNull(update.finished_at),
    // agents before 0.1.15 put the distro name here; only a real version string counts
    os_version: /^v?\d+\.\d+\.\d+/.test(str(live.os_version)) ? (str(live.os_version).split('-')[0] ?? '') : '',
    // a unit that is off has no level, and neither does one whose last telemetry row is older than two intervals
    audio_dbfs: online && telemetryFresh(live.telemetry_ts) && Array.isArray(tele.audio_dbfs) ? numOrNull(tele.audio_dbfs[0]) : null,
    uptime_s: num(tele.uptime_s),
    address: lanIps[0] ?? null,
    uplink,
    hotspot_ssid: strOrNull(live.hotspot_ssid),
    enrolled_at: secs(d.enrolled_at ?? d.created_at) ?? 0,
    key_id: str(d.key_id),
    locked,
    update_locked: typeof d.update_locked === 'boolean' ? d.update_locked : null,
  };
}

// ---- org ------------------------------------------------------------------------------------------

/** `MemberOut` -> `Member`. Invites (InviteOut) become `status: 'invited'` rows with an `invite:` id. */
export function normalizeMember(raw: unknown): Member {
  const m = obj(raw);
  if (typeof m.status === 'string' && 'mfa' in m) return m as Member;
  return {
    id: str(m.user_id),
    name: str(m.name) || str(m.email) || str(m.username),
    email: str(m.email),
    username: strOrNull(m.username),
    role: asRole(m.role),
    devices: asScope(m.scope)?.devices ?? 'all',
    scope: asScope(m.scope),
    mfa: !!m.totp_enabled,
    last_seen: secs(m.last_login_at),
    status: 'active',
  };
}
export const INVITE_PREFIX = 'invite:';
export function memberFromInvite(raw: unknown): Member {
  const i = obj(raw);
  if (typeof i.status === 'string' && 'mfa' in i) return i as Member;
  return {
    id: `${INVITE_PREFIX}${str(i.id)}`,
    name: str(i.email),
    email: str(i.email),
    role: asRole(i.role),
    devices: 'all',
    mfa: false,
    last_seen: null,
    status: 'invited',
  };
}

/** `AuditOut` -> `AuditEntry`. */
export function normalizeAudit(raw: unknown): AuditEntry {
  const a = obj(raw);
  if (typeof a.actor === 'string' && 'detail' in a) return a as AuditEntry;
  const data = obj(a.data);
  const detail = Object.entries(data)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .map(([k, v]) => `${k}=${String(v)}`)
    .join(' · ');
  return {
    id: String(a.id ?? ''),
    ts: secs(a.ts) ?? 0,
    actor: str(a.actor_user_id) || (a.actor_token_id ? `token ${str(a.actor_token_id).slice(0, 8)}` : 'system'),
    action: str(a.action),
    target: [strOrNull(a.target_type), strOrNull(a.target_id) ?? strOrNull(a.device_id)].filter(Boolean).join(' '),
    detail,
  };
}

/** `EventOut` -> `ShowEvent`. */
export function normalizeEvent(raw: unknown): ShowEvent {
  const e = obj(raw);
  if (typeof e.starts_at === 'number') return e as ShowEvent;
  return {
    id: str(e.id),
    org_id: str(e.org_id),
    name: str(e.name),
    venue: str(e.venue),
    description: str(e.description),
    tz: str(e.tz, 'UTC'),
    starts_at: secs(e.starts_at) ?? 0,
    ends_at: secs(e.ends_at) ?? 0,
  };
}

// ---- alerts ---------------------------------------------------------------------------------------

const SEVERITY: Record<string, AlertRow['severity']> = { info: 'info', warn: 'warn', warning: 'warn', crit: 'crit', critical: 'crit' };

/** `AlertOut` -> `AlertRow`. */
export function normalizeAlert(raw: unknown): AlertRow {
  const a = obj(raw);
  if (typeof a.key === 'string' && 'acked_at' in a) return a as AlertRow;
  return {
    id: str(a.id),
    device_id: str(a.device_id),
    key: str(a.kind),
    state: a.status === 'resolved' ? 'resolved' : 'firing',
    severity: SEVERITY[str(a.severity)] ?? 'warn',
    detail: str(a.message) || str(a.kind),
    ts: secs(a.last_seen_at ?? a.first_seen_at) ?? 0,
    count: num(a.count, 1),
    acked_at: secs(a.acked_at),
    acked_by: strOrNull(obj(a.data).acked_by),
  };
}

/** `GET /api/alerts/channels` (ChannelOut[]) -> the three fixed slots the Alerts screen shows. */
export function normalizeChannels(raw: unknown): AlertChannels {
  if (!Array.isArray(raw) && obj(raw).email) return raw as AlertChannels;
  const empty = { enabled: false, to: '', severities: 'all' as const };
  const out: AlertChannels = { email: { ...empty }, webhook: { ...empty } };
  for (const c of arr(raw)) {
    const kind = str(c.kind) as keyof AlertChannels;
    if (!(kind in out)) continue;
    const cfg = obj(c.config);
    const sev = str(c.min_severity);
    out[kind] = {
      enabled: !!c.enabled && !c.disabled_at,
      to: str(cfg.to ?? cfg.url ?? cfg.email ?? cfg.number ?? cfg.address),
      severities: sev === 'crit' || sev === 'critical' ? 'crit' : sev === 'warn' || sev === 'warning' ? 'warn' : 'all',
    };
  }
  return out;
}

// ---- recordings -----------------------------------------------------------------------------------

/** `RecordingOut` -> `Recording` (FileEntry + cloud columns). */
export function normalizeRecording(raw: unknown): Recording {
  const r = obj(raw);
  if ('start_ts' in r && typeof r.upload_status === 'string') return r as Recording;
  const up = str(r.upload_status);
  const upload_status: Recording['upload_status'] = r.deleted_on_device
    ? 'deleted_on_device'
    : up === 'uploaded' || up === 'synced' || up === 'complete'
      ? 'synced'
      : up === 'uploading' || up === 'requested' || up === 'partial'
        ? 'uploading'
        : 'local_only';
  return {
    id: str(r.id),
    device_id: str(r.device_id),
    name: str(r.name),
    display_name: strOrNull(r.display_name),
    start_ts: secs(r.started_at),
    end_ts: secs(r.ended_at),
    size: num(r.size_bytes),
    events: 0,
    sha256: null,
    drive: 'cloud',
    has_audio: upload_status === 'synced',
    tags: Array.isArray(r.tags) ? r.tags.map(String) : [],
    protected: false,
    event_name: strOrNull(r.event_name),
    folder: '',
    words: 0,
    tc_start: '',
    framerate: 0,
    upload_status,
  };
}

// ---- releases / rollouts --------------------------------------------------------------------------

/** `ReleaseOut` -> `Release`. */
export function normalizeRelease(raw: unknown): Release {
  const r = obj(raw);
  if ('min_agent_version' in r) return r as Release;
  const man = obj(r.manifest);
  const channel = (['stable', 'beta', 'canary'] as const).includes(r.channel) ? r.channel : 'stable';
  return {
    id: str(r.id),
    version: str(r.version),
    kind: r.kind === 'os' ? 'os' : 'app',
    channel,
    published_at: secs(r.published_at ?? r.created_at) ?? 0,
    size_bytes: num(r.size_bytes),
    notes: str(r.notes),
    signed: !!r.signed || !!r.signed_by_key_id,
    min_agent_version: str(man.min_agent_version ?? man.min_version),
    requires_reboot: r.kind === 'os' || !!man.requires_reboot,
  };
}

const UPDATE_STATE: Record<string, UpdateState> = {
  pending: 'available',
  queued: 'available',
  offered: 'available',
  sent: 'downloading',
  downloading: 'downloading',
  staged: 'staged',
  installing: 'installing',
  switching: 'switching',
  restarting: 'restarting',
  healthchecking: 'healthchecking',
  ok: 'success',
  done: 'success',
  success: 'success',
  rollback: 'rollback',
  rolled_back: 'rollback',
  failed: 'failed',
  error: 'failed',
  skipped: 'skipped',
};
const DONE_STATES = new Set<UpdateState>(['success', 'failed', 'skipped', 'rollback']);

/** `UpdateStatusOut` (per-device rollout row) -> `RolloutDevice`. */
export function rolloutDeviceOf(raw: unknown, targetVersion: string): RolloutDevice {
  const u = obj(raw);
  const state = UPDATE_STATE[str(u.state)] ?? 'available';
  const update: UpdateStatus = {
    state,
    kind: 'app',
    current_version: '',
    previous_version: '',
    target_version: targetVersion,
    staged_version: state === 'staged' ? targetVersion : '',
    progress_pct: state === 'success' ? 100 : null,
    last_error: state === 'failed' ? strOrNull(u.detail) : null,
    error: state === 'failed' || state === 'skipped' ? strOrNull(u.detail) : null,
    rollback_available: false,
    rollout_id: strOrNull(u.rollout_id),
    started_at: null,
    finished_at: DONE_STATES.has(state) ? secs(u.updated_at) : null,
    ts: secs(u.updated_at) ?? 0,
  };
  return { device_id: str(u.device_id), update };
}

const ROLLOUT_STATUS: Record<string, Rollout['status']> = {
  draft: 'pending',
  pending: 'pending',
  scheduled: 'pending',
  running: 'running',
  paused: 'paused',
  done: 'done',
  completed: 'done',
  finished: 'done',
  failed: 'failed',
  aborted: 'cancelled',
  cancelled: 'cancelled',
  rolled_back: 'cancelled',
};

/** `RolloutOut` (+ `/devices` rows, + the release version) -> `Rollout`. */
export function normalizeRollout(raw: unknown, devices: unknown = [], version = ''): Rollout {
  const r = obj(raw);
  if (typeof r.progress_pct === 'number' && Array.isArray(r.devices)) return r as Rollout;
  const t = obj(r.target);
  const kind = (['all', 'channel', 'groups', 'devices'] as const).includes(t.kind) ? t.kind : 'all';
  const ids = (kind === 'devices' ? t.device_ids : kind === 'groups' ? t.group_ids : t.ids) ?? [];
  const policy = obj(r.policy);
  const status = ROLLOUT_STATUS[str(r.status)] ?? 'pending';
  const rows = arr(devices).map((d) => rolloutDeviceOf(d, version));
  const finished = rows.filter((d) => DONE_STATES.has(d.update.state)).length;
  const progress_pct = rows.length ? Math.round((finished / rows.length) * 100) : status === 'done' ? 100 : 0;
  return {
    id: str(r.id),
    release_id: str(r.release_id),
    version,
    target: { kind, ids: Array.isArray(ids) ? ids.map(String) : [] },
    policy: policy.skip_if_engine_running === false ? 'immediate' : 'skip_if_engine_running',
    status,
    progress_pct,
    devices: rows,
    created_at: secs(r.created_at) ?? 0,
  };
}

// ---- gateway frames -------------------------------------------------------------------------------

const GW_TOPICS = new Set<string>(['snapshot', 'telemetry', 'levels', 'health', 'caption_event', 'cmd_result', 'alert', 'file_sync', 'update_status', 'settings_changed', 'presence']);

/**
 * Patch a fleet row from a live event. Presence wins: the `state` frame on subscribe replays the last cached
 * telemetry, which still says `engine_running` for a unit that was captioning when it was switched off.
 */
export function applyLiveEvent(d: Device, ev: ClientEvent): Device {
  if (ev.topic === 'presence') {
    const online = !!(ev.data as { online?: boolean }).online;
    const lastSeen = (ev.data as { last_seen?: number | null }).last_seen;
    return { ...d, online, last_seen: lastSeen ?? d.last_seen, state: online ? (d.state === 'offline' ? 'standby' : d.state) : 'offline', asr_model: online ? (d.asr_model === 'offline' ? 'idle' : d.asr_model) : 'offline', audio_dbfs: online ? d.audio_dbfs : null, cloud_rtt_ms: online ? d.cloud_rtt_ms : null };
  }
  if (ev.topic === 'update_status') {
    const u = obj(ev.data);
    const update_state = effectiveUpdateState(u);
    return {
      ...d,
      update_state,
      update_target: strOrNull(u.target_version) || strOrNull(u.staged_version) || d.update_target,
      update_pct: numOrNull(u.progress_pct),
      update_error: strOrNull(u.last_error),
      update_finished_at: numOrNull(u.finished_at),
      agent_version: typeof u.current_version === 'string' && u.current_version ? u.current_version : d.agent_version,
      state: fleetState(d.online, d.locked, update_state, d.state === 'live'),
    };
  }
  if (ev.topic === 'levels') {
    // 4 Hz meter while the fleet page is open (the gateway turns the relay on per subscribed unit)
    const dbfs = (ev.data as { dbfs?: unknown }).dbfs;
    return d.online && Array.isArray(dbfs) && typeof dbfs[0] === 'number' ? { ...d, audio_dbfs: dbfs[0] } : d;
  }
  if (ev.topic === 'telemetry') {
    const t = obj(ev.data);
    const running = !!t.engine_running;
    const dbfs = Array.isArray(t.audio_dbfs) ? t.audio_dbfs : [];
    // The level is only ever the unit's current one: an offline row keeps none (the subscribe-time `state` replay
    // carries the last telemetry a unit sent before it went away), and neither does a row older than 90 s.
    const levelOk = d.online && telemetryFresh(ev.ts / 1000) && typeof dbfs[0] === 'number';
    return {
      ...d,
      state: fleetState(d.online, d.locked, d.update_state, running),
      agent_version: str(t.agent_version, d.agent_version),
      uptime_s: typeof t.uptime_s === 'number' ? t.uptime_s : d.uptime_s,
      audio_dbfs: levelOk ? (dbfs[0] as number) : null,
      uplink: (['eth', 'wifi', 'lte', 'none'] as const).includes(t.uplink) ? t.uplink : d.uplink,
      asr_model: !d.online ? 'offline' : running ? 'running' : 'idle',
      dg_rtt_ms: typeof t.dg_latency_ms === 'number' ? t.dg_latency_ms : d.dg_rtt_ms,
      cloud_rtt_ms: typeof t.cloud_rtt_ms === 'number' ? t.cloud_rtt_ms : d.cloud_rtt_ms,
    };
  }
  return d;
}

/**
 * One `/gw/browser` text frame -> UI events. Frames are `{device_id, t, ts, d}`; the `state` frame
 * sent on subscribe carries presence + the device's live hash and becomes presence/telemetry/health
 * events so a freshly opened screen has data before the next tick.
 */
export function gatewayFrameToEvents(msg: unknown): ClientEvent[] {
  const m = obj(msg);
  const t = str(m.t);
  const deviceId = str(m.device_id) as DeviceId;
  if (!t || !deviceId) return [];
  const ts = typeof m.ts === 'number' ? m.ts * (m.ts < 1e12 ? 1000 : 1) : Date.now();
  const ev = (topic: Topic, data: unknown): ClientEvent => ({ topic, deviceId, ts, data }) as ClientEvent;
  if (t === 'state') {
    const d = obj(m.d);
    const live = obj(d.live);
    const out: ClientEvent[] = [ev('presence', { online: !!d.online, session_id: null, last_seen: null })];
    if (live.telemetry) out.push(ev('telemetry', live.telemetry));
    if (live.health) out.push(ev('health', live.health));
    if (live.update) out.push(ev('update_status', live.update));
    return out;
  }
  if (t === 'presence') {
    const d = obj(m.d);
    return [ev('presence', { online: !!d.online, session_id: null, last_seen: d.online ? null : ts / 1000, gw_id: strOrNull(d.gw) ?? undefined })];
  }
  if (GW_TOPICS.has(t)) return [ev(t as Topic, m.d)];
  if (t === 'error') console.warn('[gw]', deviceId, m.d);
  return [];
}

/** `PresetOut` -> `Preset`. */
export function normalizePreset(raw: unknown): Preset {
  const p = obj(raw);
  if (typeof p.created_at === 'number') return p as Preset;
  return {
    id: str(p.id),
    org_id: str(p.org_id),
    name: str(p.name),
    description: str(p.description),
    sections: Array.isArray(p.sections) ? p.sections.map(String) : [],
    settings: obj(p.settings) as Preset['settings'],
    wifi: arr(p.wifi).map((w) => ({ ssid: str(obj(w).ssid), hidden: !!obj(w).hidden, has_password: !!obj(w).has_password })),
    source: str(p.source),
    created_at: secs(p.created_at) ?? 0,
    updated_at: secs(p.updated_at) ?? 0,
  };
}

export function normalizeSchedule(raw: unknown): CaptionSchedule {
  const e = obj(raw);
  return {
    id: str(e.id),
    org_id: str(e.org_id),
    name: str(e.name),
    enabled: e.enabled !== false,
    tz: str(e.tz, 'UTC'),
    start_local: str(e.start_local, '18:00'),
    end_local: str(e.end_local, '07:30'),
    days: Array.isArray(e.days) ? e.days.map((d) => Number(d)).filter((d) => Number.isInteger(d)) : [0, 1, 2, 3, 4, 5, 6],
    device_ids: Array.isArray(e.device_ids) ? e.device_ids.map((x) => String(x)) : [],
    event_ids: Array.isArray(e.event_ids) ? e.event_ids.map((x) => String(x)) : [],
    created_at: secs(e.created_at) ?? 0,
    updated_at: secs(e.updated_at) ?? 0,
    active_now: e.active_now === true,
  };
}

export function normalizeScheduleState(raw: unknown): CaptionScheduleState {
  const e = obj(raw);
  return {
    device_id: str(e.device_id),
    desired: e.desired === 'off' || e.desired === 'on' ? e.desired : null,
    schedule_id: strOrNull(e.schedule_id),
    schedule_name: strOrNull(e.schedule_name),
    until: secs(e.until),
    override: e.override === true,
    applied: e.applied === true,
    last_error: strOrNull(e.last_error),
  };
}
